import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { accountApi } from "../api/accountApi";
import { normalizeApiError } from "@api/normalizeApiError";
import { AccountStatusContext } from "../contexts/accountStatusContext";
import { useAuth } from "@features/auth/hooks/useAuth";

export const AccountStatusProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user, isReady, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountStatusError, setAccountStatusError] = useState<string | null>(
    null,
  );
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [emailVerifiedAt, setEmailVerifiedAt] = useState<string | null>(null);
  const isMounted = useRef(true);
  const verificationRef = useRef<{
    emailVerified: boolean | null;
    emailVerifiedAt: string | null;
  }>({
    emailVerified: null,
    emailVerifiedAt: null,
  });

  const isAuthed = Boolean(isReady && user);

  const logVerificationTransition = useCallback(
    (
      previous: {
        emailVerified: boolean | null;
        emailVerifiedAt: string | null;
      },
      next: { emailVerified: boolean | null; emailVerifiedAt: string | null },
      reason: string,
    ) => {
      if (!import.meta.env.DEV) return;
      console.info("[auth verification] transition", {
        previous,
        next,
        reason,
      });
    },
    [],
  );

  const setVerificationState = useCallback(
    (
      next: { emailVerified: boolean | null; emailVerifiedAt: string | null },
      reason: string,
    ) => {
      const previous = verificationRef.current;
      verificationRef.current = next;
      setEmailVerified(next.emailVerified);
      setEmailVerifiedAt(next.emailVerifiedAt);
      logVerificationTransition(previous, next, reason);
    },
    [logVerificationTransition],
  );

  const refresh = useCallback(async () => {
    if (!isAuthed) return;
    setIsLoading(true);
    setError(null);
    setAccountStatusError(null);
    try {
      const result = await accountApi.getSecurityState();
      if (isMounted.current) {
        if (result.noChange) {
          setAccountStatusError(
            "We couldn't refresh your verification status right now. Please retry.",
          );
          logVerificationTransition(
            verificationRef.current,
            verificationRef.current,
            "no-change",
          );
          return;
        }

        const verified = Boolean(result.data.emailVerified);
        const verifiedAt = result.data.emailVerifiedAt ?? null;
        const next = { emailVerified: verified, emailVerifiedAt: verifiedAt };
        setVerificationState(next, "refresh-success");
        updateUser((current) => ({
          ...current,
          email_verified: verified,
          email_verified_at: verifiedAt,
        }));
      }
    } catch (err) {
      if (isMounted.current) {
        const message = normalizeApiError(err).message;
        setError(message);
        setAccountStatusError(message);
        logVerificationTransition(
          verificationRef.current,
          verificationRef.current,
          "transient-error",
        );
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [isAuthed, logVerificationTransition, setVerificationState, updateUser]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      setIsLoading(false);
      setError(null);
      setAccountStatusError(null);
      setVerificationState(
        { emailVerified: null, emailVerifiedAt: null },
        "explicit-logout",
      );
      return;
    }

    const nextFromUser = {
      emailVerified: user?.email_verified ?? null,
      emailVerifiedAt: user?.email_verified_at ?? null,
    };
    setVerificationState(nextFromUser, "auth-user-sync");
    void refresh();
  }, [
    isAuthed,
    refresh,
    setVerificationState,
    user?.email_verified,
    user?.email_verified_at,
  ]);

  const value = useMemo(
    () => ({
      isLoading,
      error,
      accountStatusError,
      emailVerified,
      emailVerifiedAt,
      refresh,
    }),
    [
      isLoading,
      error,
      accountStatusError,
      emailVerified,
      emailVerifiedAt,
      refresh,
    ],
  );

  return (
    <AccountStatusContext.Provider value={value}>
      {children}
    </AccountStatusContext.Provider>
  );
};
